import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { FOCUSABLE, returnFocus, showsFocusRing, trapTab } from './focus'
import { Icon } from './Icon'

interface DialogProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  className?: string
  children: ReactNode
  /** Element id used for aria-labelledby. */
  titleId?: string
  closeLabel?: string
  /** false: a click outside does not close it (e.g. a secret shown once). Escape and the close button still do. */
  closeOnBackdrop?: boolean
  /** Keys for the content (e.g. arrow keys), before Escape and Tab: preventDefault to take a key. */
  onKeyDown?: (event: KeyboardEvent<HTMLDialogElement>) => void
  /**
   * 'bar': a screen's slim top bar, the way iPhone forms are laid out: Cancel on the left, the title in the middle and
   * `action` (a Create button, say) on the right, where it stays in sight with the keyboard up. The description is kept
   * for screen readers only. 'card' (the default): the title, its description and a close button.
   */
  chrome?: 'card' | 'bar'
  action?: ReactNode
  /** 'bar' only: no Cancel on the left, when `action` is the one way out (Done once something is finished). Escape still closes. */
  hideClose?: boolean
  /** Where focus goes as it closes, instead of what had it as it opened (a text field whose keyboard should come back, say). */
  focusOnClose?: HTMLElement | null
}

/**
 * Native <dialog> shown modally (inert background), with an explicit Tab focus trap, Escape to
 * close, backdrop click to close and focus restored to the opener.
 */
export function Dialog({ open, onClose, title, description, className, children, titleId, closeLabel, closeOnBackdrop = true, onKeyDown, chrome = 'card', action, focusOnClose, hideClose = false }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)
  const onCloseRef = useRef(onClose)
  const focusOnCloseRef = useRef(focusOnClose)
  const id = titleId ?? `dialog-title-${String(title).replace(/\W+/g, '-').toLowerCase()}`

  useEffect(() => {
    onCloseRef.current = onClose
    focusOnCloseRef.current = focusOnClose
  }, [onClose, focusOnClose])

  useEffect(() => {
    const dialog = ref.current
    if (!dialog || !open) return
    const opener = focusOnCloseRef.current ?? (document.activeElement as HTMLElement | null)
    // Whether it showed a ring, so focus goes back the way it was: without one after a tap (see returnFocus).
    const openerRing = showsFocusRing(opener)
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal()
      else dialog.setAttribute('open', '')
    }
    const autofocus = dialog.querySelector<HTMLElement>('[autofocus], [data-autofocus]')
    // A screen with a top bar takes focus itself: its first button is Cancel, and a ring round that reads as pressed.
    ;(autofocus ?? (dialog.classList.contains('dialog-screen') ? dialog : dialog.querySelector<HTMLElement>(FOCUSABLE)))?.focus()
    return () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
      returnFocus(opener, openerRing)
    }
  }, [open])

  if (!open) return null

  return (
    <dialog
      ref={ref}
      className={chrome === 'bar' ? `dialog-screen ${className ?? ''}`.trim() : className}
      tabIndex={chrome === 'bar' ? -1 : undefined}
      aria-labelledby={id}
      onCancel={(event) => {
        event.preventDefault()
        onCloseRef.current()
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event)
        if (event.defaultPrevented) return
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          onCloseRef.current()
          return
        }
        trapTab(event, ref.current)
      }}
      onMouseDown={(event) => {
        // Clicking the backdrop targets the dialog element itself.
        if (closeOnBackdrop && event.target === ref.current) {
          const rect = ref.current.getBoundingClientRect()
          const inside =
            event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom
          if (!inside) onCloseRef.current()
        }
      }}
    >
      {chrome === 'bar' ? (
        <header className="dialog-bar">
          {hideClose ? (
            <span aria-hidden="true" />
          ) : (
            <button type="button" className="bar-button" onClick={() => onCloseRef.current()}>
              {closeLabel ?? 'Cancel'}
            </button>
          )}
          <h2 id={id}>{title}</h2>
          <span className="dialog-bar-action">{action}</span>
          {description ? <p className="sr-only">{description}</p> : null}
        </header>
      ) : (
        <div className="dialog-header">
          <div>
            <h2 id={id}>{title}</h2>
            {description ? <p className="dialog-description">{description}</p> : null}
          </div>
          <button type="button" className="icon-button" onClick={() => onCloseRef.current()} aria-label={closeLabel ?? 'Close'}>
            <Icon name="x" />
          </button>
        </div>
      )}
      {children}
    </dialog>
  )
}
